import { useTranslation } from 'next-i18next';

import { type BuildTranslationKeys } from '@/types/translation';

/**
 * 숫자 스케일 타입
 * - million: 백만 단위 (1,000,000 이상)
 * - tenThousand: 만 단위 (10,000 이상, 한국어/일본어 전용)
 * - thousand: 천 단위 (1,000 이상)
 * - normal: 일반 숫자 (1,000 미만)
 */
type NumberScale = 'million' | 'tenThousand' | 'thousand' | 'normal';

interface ScaledNumber {
  value: number;
  scale: NumberScale;
}

/**
 * 조회수를 로케일에 맞게 포맷팅하는 훅
 *
 * 숫자 계산과 i18n을 분리하여 번역 파일에서 포맷 관리:
 * - 계산: 숫자를 적절한 단위로 나눔 (예: 1234567 → 123.4, scale: 'tenThousand')
 * - i18n: common:number.{scale} 키로 번역 (예: "{{value}}만")
 *
 * @returns formatViewCount - 조회수를 포맷팅하는 함수
 *
 * @example
 * ```tsx
 * const formatViewCount = useFormatViewCount();
 * formatViewCount(1234567);
 * // ko: "123.4만" (from common:number.tenThousand)
 * // en: "1.2M" (from common:number.million)
 * ```
 */
export const useFormattedViewCount = (viewCount: number) => {
  const { t } = useTranslation(['build']);

  const { value, scale } = calculateScale(viewCount);

  // normal scale은 그냥 숫자 반환
  if (scale === 'normal') {
    return viewCount.toString();
  }

  return t(`build:youtube.viewCount.${scale}` as BuildTranslationKeys, {
    value: value.toFixed(0),
  });
};

/**
 * 숫자를 적절한 스케일로 계산
 *
 * 우선순위:
 * 1. million (1,000,000 이상): 백만 단위로 나눔
 * 2. tenThousand (10,000 이상): 만 단위로 나눔 (한국어/일본어 지원)
 * 3. thousand (1,000 이상): 천 단위로 나눔
 * 4. normal (1,000 미만): 그대로 반환
 */
function calculateScale(count: number): ScaledNumber {
  if (count >= 10_000) {
    return {
      value: Math.floor(count / 1_000) / 10,
      scale: 'tenThousand',
    };
  }

  if (count >= 1_000) {
    return {
      value: Math.floor(count / 100) / 10,
      scale: 'thousand',
    };
  }

  return {
    value: count,
    scale: 'normal',
  };
}
